import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, HelpCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useRBACPermissions } from '../hooks/useRBACPermissions';
import { useAgentHealth } from '../hooks/useAgentHealth';
import './AdminLandingPage.css';

interface AgentCardProps {
  id: string;
  displayName: string;
  icon: string;
  description: string;
  health?: {
    status: string;
    responseTime?: number;
    lastChecked?: Date | null;
    error?: string;
  };
  onClick: () => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ displayName, icon, description, health, onClick }) => (
  <div className="admin-agent-card" onClick={onClick}>
    <div className="agent-card-header">
      <div className="agent-icon">
        {icon.startsWith('/') ? (
          <img src={icon} alt={displayName} className="agent-logo" />
        ) : (
          <span className="agent-emoji">{icon}</span>
        )}
      </div>
      <div className="agent-info">
        <h3 className="agent-name">{displayName}</h3>
        <p className="agent-description">{description}</p>
      </div>
      <div className={`health-badge ${health?.status || 'unknown'}`}>
        {health?.status === 'healthy' && (
          <>
            <CheckCircle className="h-4 w-4 inline mr-1" />
            Healthy
          </>
        )}
        {health?.status === 'unhealthy' && (
          <>
            <XCircle className="h-4 w-4 inline mr-1" />
            Unhealthy
          </>
        )}
        {health?.status === 'checking' && (
          <>
            <Loader2 className="h-4 w-4 inline mr-1 animate-spin" />
            Checking
          </>
        )}
        {(!health || health?.status === 'unknown') && (
          <>
            <HelpCircle className="h-4 w-4 inline mr-1" />
            Unknown
          </>
        )}
      </div>
    </div>
    
    <div className="agent-card-body">
      {health?.responseTime && (
        <div className="metric">
          <span className="metric-label">Response Time</span>
          <span className={`metric-value ${health.responseTime > 5000 ? 'slow' : health.responseTime > 2000 ? 'medium' : 'fast'}`}>
            {health.responseTime}ms
          </span>
        </div>
      )}
      
      {health?.lastChecked && (
        <div className="last-checked">
          Last checked: {health.lastChecked.toLocaleTimeString()}
        </div>
      )}
      
      {health?.error && (
        <div className="health-error" title={health.error}>
          ⚠️ {health.error.length > 50 ? `${health.error.substring(0, 50)}...` : health.error}
        </div>
      )}
    </div>
    
    <div className="card-footer">
      <span className="click-hint">Click to view dashboard →</span>
    </div>
  </div>
);

// Move agents array outside component to prevent re-creation on every render
const MONITORED_AGENTS = ['aldo', 'hrpolicy', 'pwms-support', 'product', 'unifirst_docs'];

const AdminLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    user,
    hasAdminAccess, 
    hasFullAdminAccess, 
    isTestingAdmin, 
    canAccessPolicyTesting, 
    canAccessPWMSTesting 
  } = useAuth();

  // RBAC permissions for agent-specific admin access
  const { 
    permissions, 
    isSuperAdmin, 
    isAgentAdmin,
    isLoading: rbacLoading 
  } = useRBACPermissions();

  // Health monitoring for all agents
  const { getAgentHealth } = useAgentHealth({
    agents: MONITORED_AGENTS,
    checkInterval: 3600000, // Check every hour
    enablePeriodicChecking: true
  });

  // Filter agents based on permissions
  const availableAgents = React.useMemo(() => {
    interface AgentConfig {
      id: string;
      displayName: string;
      icon: string;
      description: string;
      route: string;
      adminRole: string;
    }

    const agents: AgentConfig[] = [];
    
    // Define all possible agent configs
    const agentConfigs: AgentConfig[] = [
      {
        id: 'aldo',
        displayName: 'Scout',
        icon: '/assets/logos/aldo-no-back.png',
        description: 'General AI assistant for UniFirst employees',
        route: '/admin/aldo',
        adminRole: 'aldo-admin'
      },
      {
        id: 'hrpolicy',
        displayName: 'PolicyTech',
        icon: '/assets/logos/aldo-hr-policy.png',
        description: 'HR policies, procedures, and employee guidance',
        route: '/admin/hrpolicy',
        adminRole: 'hr-admin'
      },
      {
        id: 'pwms-support',
        displayName: 'PWMS',
        icon: '/assets/logos/aldo-no-back.png',
        description: 'Production Workforce Management System support',
        route: '/admin/pwms',
        adminRole: 'pwms-admin'
      },
      {
        id: 'product',
        displayName: 'Product',
        icon: '/assets/logos/aldo-product.png',
        description: 'Product information, support, and guidance',
        route: '/admin/product',
        adminRole: 'product-admin'
      },
      {
        id: 'unifirst_docs',
        displayName: 'UniFirst Documentation',
        icon: '/assets/logos/aldounifirstdocs.png',
        description: 'UniFirst internal documentation assistant',
        route: '/admin/unifirst_docs',
        adminRole: 'unfdocs-admin'
      },
      {
        id: 'orientation',
        displayName: 'Orientation',
        icon: '/assets/logos/aldo-orientation.png',
        description: 'New employee orientation assistant',
        route: '/admin/orientation',
        adminRole: 'orientation-admin'
      },
      {
        id: 'iris',
        displayName: 'IRIS',
        icon: '/assets/logos/aldo-iris.png',
        description: 'IRIS system support assistant',
        route: '/admin/iris',
        adminRole: 'iris-admin'
      },
      {
        id: 'service-desk',
        displayName: 'Service Desk',
        icon: '/assets/logos/aldo-service-desk.png',
        description: 'IT Service Desk support assistant',
        route: '/admin/service-desk',
        adminRole: 'service-desk-admin'
      },
      {
        id: 'owensboro2',
        displayName: 'Owensboro Production',
        icon: '/assets/logos/aldo-embroidery.png',
        description: 'Owensboro production facility assistant',
        route: '/admin/owensboro2',
        adminRole: 'owensboro2-admin'
      },
      {
        id: 'direct_embroidery',
        displayName: 'Direct Embroidery',
        icon: '/assets/logos/aldo-embroidery.png',
        description: 'Direct embroidery operations assistant',
        route: '/admin/direct_embroidery',
        adminRole: 'direct-embroidery-admin'
      },
      {
        id: 'firstaid-safety',
        displayName: 'First Aid & Safety',
        icon: '🧯',
        description: 'NFPA 10 fire extinguisher standards and compliance',
        route: '/admin/firstaid-safety',
        adminRole: 'firstaid-safety-admin'
      }
    ];
    
    // Get user roles from auth
    const userRoles = (user as any)?.userRoles || [];
    
    // Check if user is a super admin (sees ALL agents)
    const isSuperAdminRole = userRoles.some((role: string) => 
      role === 'admin' || role === 'super-admin' || role === 'askaldo-admin'
    );
    
    // Debug logging
    console.log('🔐 [AdminLandingPage] Role check:', {
      userRoles,
      isSuperAdminRole,
      isSuperAdmin: isSuperAdmin(),
      hasFullAdminAccess: hasFullAdminAccess()
    });
    
    // Super admins see ALL agents
    if (isSuperAdminRole || isSuperAdmin() || hasFullAdminAccess()) {
      console.log('🔐 [AdminLandingPage] User has full admin access - showing all agents');
      agents.push(...agentConfigs);
    } 
    // Agent-specific admins only see their specific agent(s)
    else {
      // Map agent-specific admin roles to their agents
      // Role names must match those in DevTestingPanel.tsx
      const roleAgentMap: Record<string, string> = {
        'hr-admin': 'hrpolicy',
        'pwms-admin': 'pwms-support',
        'product-admin': 'product',
        'aldo-admin': 'aldo',
        'unfdocs-admin': 'unifirst_docs',
        'orientation-admin': 'orientation',
        'iris-admin': 'iris',
        'service-desk-admin': 'service-desk',
        'owensboro2-admin': 'owensboro2',
        'direct-embroidery-admin': 'direct_embroidery'
      };
      
      // Add agents for each admin role the user has
      console.log('🔐 [AdminLandingPage] Agent-specific admin - checking role mapping');
      userRoles.forEach((role: string) => {
        const agentId = roleAgentMap[role];
        console.log(`   Role: ${role} -> Agent: ${agentId || 'not mapped'}`);
        if (agentId) {
          const agent = agentConfigs.find(a => a.id === agentId);
          if (agent && !agents.some(a => a.id === agent.id)) {
            agents.push(agent);
            console.log(`   ✅ Added agent: ${agent.displayName}`);
          }
        }
      });
      
      // Legacy testing admin access (fallback)
      if (isTestingAdmin()) {
        if (canAccessPolicyTesting()) {
          const hrAgent = agentConfigs.find(a => a.id === 'hrpolicy');
          if (hrAgent && !agents.some(a => a.id === 'hrpolicy')) agents.push(hrAgent);
        }
        if (canAccessPWMSTesting()) {
          const pwmsAgent = agentConfigs.find(a => a.id === 'pwms-support');
          if (pwmsAgent && !agents.some(a => a.id === 'pwms-support')) agents.push(pwmsAgent);
        }
      }
    }
    
    return agents;
  }, [user, isSuperAdmin, hasFullAdminAccess, isTestingAdmin, canAccessPolicyTesting, canAccessPWMSTesting]);

  const handleAgentClick = (route: string) => {
    navigate(route);
  };

  // Auto-redirect: Super admins go directly to Aldo, single-agent admins go to their agent
  // This hook MUST be called before any early returns
  const shouldRedirect = !rbacLoading && (
    availableAgents.length === 1 || 
    isSuperAdmin() || 
    hasFullAdminAccess()
  );
  
  React.useEffect(() => {
    console.log('🔐 [AdminLandingPage] Navigation check:', {
      rbacLoading,
      availableAgentsCount: availableAgents.length,
      availableAgentIds: availableAgents.map(a => a.id),
      isSuperAdmin: isSuperAdmin(),
      hasFullAdminAccess: hasFullAdminAccess()
    });
    
    if (rbacLoading) return;
    
    if (availableAgents.length === 1) {
      // Single agent admin - go directly to their agent
      console.log(`🔐 [AdminLandingPage] Navigating to single agent: ${availableAgents[0].route}`);
      navigate(availableAgents[0].route, { replace: true });
    } else if (isSuperAdmin() || hasFullAdminAccess()) {
      // Super admins go directly to Aldo dashboard
      console.log('🔐 [AdminLandingPage] Navigating to Aldo (super admin)');
      navigate('/admin/aldo', { replace: true });
    }
  }, [availableAgents, isSuperAdmin, hasFullAdminAccess, navigate, rbacLoading]);

  if (!hasAdminAccess() && !isAgentAdmin()) {
    return (
      <div className="admin-landing-container">
        <div className="access-denied">
          <h2>🔒 Admin Access Required</h2>
          <p>You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (rbacLoading) {
    return (
      <div className="admin-landing-container">
        <div className="loading">
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  if (shouldRedirect) {
    return (
      <div className="admin-landing-container">
        <div className="loading">
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-landing-container">
      <div className="admin-landing-header">
        <h1>Admin Dashboard</h1>
        <p>Select an agent to view analytics, feedback, and testing data</p>
        
        <div className="admin-badges">
          {(isSuperAdmin() || (user as any)?.userRoles?.includes('super-admin')) && (
            <span className="admin-badge full">Super Admin</span>
          )}
          {!isSuperAdmin() && !(user as any)?.userRoles?.includes('super-admin') && hasFullAdminAccess() && (
            <span className="admin-badge full">Admin</span>
          )}
          {(() => {
            const userRoles = (user as any)?.userRoles || [];
            const agentAdminRoles = userRoles.filter((role: string) => 
              role.endsWith('-admin') && !['super-admin', 'askaldo-admin'].includes(role)
            );
            if (agentAdminRoles.length > 0 && !isSuperAdmin() && !hasFullAdminAccess()) {
              return (
                <span className="admin-badge agent">
                  {agentAdminRoles.map((r: string) => r.replace('-admin', '')).join(', ')} Admin
                </span>
              );
            }
            return null;
          })()}
          {isTestingAdmin() && canAccessPolicyTesting() && !canAccessPWMSTesting() && (
            <span className="admin-badge policy">Policy Testing</span>
          )}
          {isTestingAdmin() && canAccessPWMSTesting() && !canAccessPolicyTesting() && (
            <span className="admin-badge pwms">PWMS Testing</span>
          )}
          {isTestingAdmin() && canAccessPolicyTesting() && canAccessPWMSTesting() && (
            <span className="admin-badge dual">Testing Admin</span>
          )}
        </div>
      </div>

      {/* Agent Factory - Super Admin Only */}
      {(isSuperAdmin() || (user as any)?.userRoles?.includes('super-admin') || (user as any)?.userRoles?.includes('askaldo-admin')) && (
        <div className="factory-section" style={{ marginBottom: '2rem' }}>
          <div 
            className="admin-agent-card factory-card"
            onClick={() => navigate('/admin/agent-factory')}
            style={{ 
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              border: '2px solid #a855f7',
              cursor: 'pointer'
            }}
          >
            <div className="agent-card-header">
              <div className="agent-icon">
                <span className="agent-emoji" style={{ fontSize: '2.5rem' }}>🏭</span>
              </div>
              <div className="agent-info">
                <h3 className="agent-name" style={{ color: 'white' }}>Agent Factory</h3>
                <p className="agent-description" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  Create, customize, and manage AI agents with the visual builder
                </p>
              </div>
              <div className="health-badge" style={{ background: '#22c55e', color: 'white' }}>
                ✨ New
              </div>
            </div>
            <div className="card-footer">
              <span className="click-hint" style={{ color: 'rgba(255,255,255,0.8)' }}>Open Agent Factory →</span>
            </div>
          </div>
        </div>
      )}

      <div className="agents-grid">
        {availableAgents.map(agent => (
          <AgentCard
            key={agent.id}
            id={agent.id}
            displayName={agent.displayName}
            icon={agent.icon}
            description={agent.description}
            health={getAgentHealth(agent.id)}
            onClick={() => handleAgentClick(agent.route)}
          />
        ))}
      </div>

      {availableAgents.length === 0 && (
        <div className="no-agents">
          <p>No agents available for your current permissions.</p>
        </div>
      )}
    </div>
  );
};

export default AdminLandingPage;